<template>
  <div>
    <Head :title="`Venta #${order.number_order ?? order.id} ${order.order_state === 'cancel' ? '| PEDIDO ANULADO' : ''}`" />

    <div class="mb-8">
      <!-- Breadcrumbs -->
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-4">
        <button class="hover:text-orange-500 transition-colors" title="Volver atrás" @click="back()">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/orders" class="hover:text-orange-500 transition-colors">Ventas</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">Venta #{{ order.number_order ?? order.id }}</span>
      </div>

      <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        <div class="flex items-center gap-3">
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
            Venta #{{ order.number_order ?? order.id }}
            <span v-if="order.order_state === 'cancel'" class="text-red-500 text-lg ml-2 align-middle font-medium">
              (ANULADO)
            </span>
          </h1>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap items-center gap-3 w-full xl:w-auto">
          <!-- Nueva Venta -->
          <a
            href="/orders/create"
            class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
            title="Nueva venta"
          >
            <i class="fa fa-plus" />
            <span class="hidden sm:inline">Nueva venta</span>
          </a>

          <!-- WhatsApp -->
          <button
            v-if="order.client && order.number_order" class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
            title="Compartir por WhatsApp"
            @click="shareOrder('whatsapp')"
          >
            <i class="fab fa-whatsapp text-green-600 text-lg" />
            <span class="hidden sm:inline">WhatsApp</span>
          </button>

          <!-- Email -->
          <button
            v-if="order.client && order.number_order" class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
            title="Compartir por correo"
            @click="shareOrder('email')"
          >
            <i class="fa fa-envelope text-gray-500" />
            <span class="hidden sm:inline">Email</span>
          </button>

          <!-- Presupuesto -->
          <a
            target="_blank" :href="`/orders/${order.id}/report`"
            class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
            title="Imprimir Presupuesto"
          >
            <i class="fa fa-print text-gray-500" />
            <span class="hidden sm:inline">Presupuesto</span>
          </a>

          <!-- Nota de Entrega -->
          <template v-if="order.order_state !== 'estimate'">
            <a
              target="_blank" :href="`/orders/${order.id}/report-storer`"
              class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
              title="Nota de entrega"
            >
              <i class="fa fa-file-alt text-gray-500" />
              <span class="hidden sm:inline">Nota Entrega</span>
            </a>
          </template>

          <!-- Ticket Térmico -->
          <a
            target="_blank" :href="`/orders/${order.id}/report-thermical`"
            class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
            title="Imprimir Ticket Térmico (58mm / 80mm)"
          >
            <i class="fa fa-receipt text-orange-500" />
            <span class="hidden sm:inline">Ticket Térmico</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Información del cliente y detalles de la orden -->
    <div
      class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 p-4 sm:p-6 bg-gradient-to-br from-orange-50 to-white rounded-2xl shadow-lg border border-orange-100"
    >
      <!-- Cliente -->
      <div class="flex flex-col gap-4 bg-white rounded-xl shadow p-4 border border-orange-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-user-circle text-2xl text-orange-500" />
          <span class="font-semibold text-orange-800 text-lg">Cliente</span>
        </div>
        <Link :href="`/clients/${order.client.id}`" class="flex items-center gap-2">
          <i class="fa fa-id-badge text-gray-400" />
          <span class="font-semibold">{{ order.client.name }}</span>
        </Link>
        <div class="flex items-center gap-2">
          <i class="fa fa-address-card text-gray-400" />
          <span class="text-gray-800 text-sm">{{ order.client.identification }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-envelope text-gray-400" />
          <span class="text-gray-800 text-sm">{{ order.client.email || '-' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-phone text-gray-400" />
          <span class="text-gray-800 text-sm">{{ order.client.phone || '-' }}</span>
        </div>
      </div>
      <!-- Totales y saldo -->
      <div class="flex flex-col gap-3 bg-white rounded-xl shadow p-4 border border-orange-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-dollar-sign text-2xl text-orange-500" />
          <span class="font-semibold text-orange-800 text-lg">Totales</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-wallet text-gray-400" />
          <span class="font-semibold">Balance:</span>
          <span>${{ (Number(order?.balance) || 0).toFixed(2) }} / Bs. {{ ((Number(order?.balance) || 0) * change).toFixed(2) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-money-bill text-gray-400" />
          <span class="font-semibold">Deuda:</span>
          <span>${{ (Number(order.total) - (Number(order?.balance) || 0)).toFixed(2) }} / Bs. {{ (((Number(order.total) - (Number(order?.balance) || 0)) *
            change)).toFixed(2)
          }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-receipt text-gray-400" />
          <span class="font-semibold">Subtotal:</span>
          <span>${{ (Number(order.subtotal) || 0).toFixed(2) }}</span>
        </div>
        <div v-if="order.discount && Number(order.discount) > 0" class="flex items-center gap-2 text-red-600">
          <i class="fa fa-tag" />
          <span class="font-semibold">Descuento:</span>
          <span>-${{ (Number(order.discount) || 0).toFixed(2) }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-coins text-gray-400" />
          <span class="font-semibold">Total:</span>
          <span>${{ Number(order.total).toFixed(2) }} / Bs. {{ (Number(order.total) * change).toFixed(2) }}</span>
        </div>
      </div>
      <!-- Info extra -->
      <div class="flex flex-col gap-3 bg-white rounded-xl shadow p-4 border border-orange-100">
        <div class="flex items-center gap-2 mb-2">
          <i class="fa fa-info-circle text-2xl text-orange-500" />
          <span class="font-semibold text-orange-800 text-lg">Detalles</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-calendar-alt text-gray-400" />
          <span class="font-semibold">Fecha:</span>
          <span>{{ order?.date }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-file-alt text-gray-400" />
          <span class="font-semibold">Orden compra:</span>
          <span>{{ order?.order_buy }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-comment text-gray-400" />
          <span class="font-semibold">Observaciones:</span>
          <span>{{ order?.comentary }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-credit-card text-gray-400" />
          <span class="font-semibold">Modalidad:</span>
          <span>{{ order.isCredit ? 'Crédito' : 'Contado' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="fa fa-flag text-gray-400" />
          <span class="font-semibold">Estado:</span>
          <span
            :class="{
              'text-yellow-600': order.order_state === 'estimate',
              'text-red-600': order.order_state === 'cancel',
              'text-orange-600': order.order_state === 'request',
              'text-green-600': order.order_state === 'finish' || order.order_state === 'finalizado',
              'text-blue-600': order.order_state === 'pay' || order.order_state === 'en_preparacion',
              'text-purple-600': order.order_state === 'delivery' || order.order_state === 'pendiente_pago',
              'text-amber-600 font-bold': order.order_state === 'por_iniciar'
            }"
          >
            {{ getOrderStateLabel(order.order_state) }}
          </span>
        </div>
        <div v-if="order.order_state === 'estimate'" class="mt-4">
          <button class="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium w-full shadow-sm" @click="changeOrderState()">
            <i class="fa fa-check mr-2" /> Procesar orden
          </button>
        </div>
        <div class="flex flex-col items-end justify-end mt-4">
          <div class="flex items-center gap-2 mb-1">
            <i class="fa fa-user-tie text-xl text-orange-500" />
            <span class="font-medium text-orange-800">Vendedor:</span>
          </div>
          <div class="font-medium text-right">
            {{ order?.responsible?.first_name }} {{ order?.responsible?.last_name }}
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de campos adicionales -->
    <div v-if="order.additional_values && order.additional_values.length > 0" class="p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-orange-100 mb-6">
      <h2 class="text-xl font-bold mb-6 flex items-center gap-2">
        <i class="fa fa-list-alt text-orange-500" /> Campos adicionales
      </h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div v-for="additionalValue in order.additional_values" :key="additionalValue.id" class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa fa-tag text-orange-500" />
            <span class="font-semibold text-gray-800">{{ additionalValue.additional.field_label }}</span>
            <!--<span v-if="additionalValue.additional.is_required" class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Requerido</span>-->
          </div>
          
          <div class="text-gray-700">
            <!-- Mostrar valor según el tipo de campo -->
            <span v-if="additionalValue.additional.field_type === 'boolean'" class="font-medium">
              {{ additionalValue.value === '1' || additionalValue.value === true ? 'Sí' : 'No' }}
            </span>
            <span v-else-if="additionalValue.additional.field_type === 'date'" class="font-medium">
              {{ formatDate(additionalValue.value) }}
            </span>
            <span v-else class="font-medium">
              {{ additionalValue.value || '-' }}
            </span>
          </div>
          
          <!--<div class="text-xs text-gray-500 mt-1">
            Tipo: {{ getFieldTypeLabel(additionalValue.additional.field_type) }}
          </div>-->
        </div>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-orange-100 mb-6">
      <h2 class="text-xl font-bold mb-6 flex items-center gap-2">
        <i class="fa fa-box text-orange-500" /> Productos
      </h2>
      <div class="overflow-x-auto rounded-lg border border-orange-100">
        <table class="w-full min-w-max">
          <thead class="bg-orange-50">
            <tr>
              <th class="py-4 px-4 sm:px-6 text-left">Código</th>
              <th class="py-4 px-4 sm:px-6 text-left">Producto</th>
              <th class="py-4 px-4 sm:px-6 text-left">Descripción</th>
              <th class="py-4 px-4 sm:px-6 text-left">Cantidad</th>
              <th class="py-4 px-4 sm:px-6 text-left">Precio</th>
              <th class="py-4 px-4 sm:px-6 text-left">Fee</th>
              <th class="py-4 px-4 sm:px-6 text-left">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(product, key) in order.products" :key="product.id" :class="[
                'border-t',
                product.dispatch ? 'bg-blue-50' : '',
                'hover:bg-gray-50 transition-colors'
              ]"
            >
              <!-- <td class="py-3 px-4 sm:px-6">
                <input v-model="products.products[key]['dispatch']" :value="product.id" type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              </td> -->
              <td class="py-3 px-4 sm:px-6">{{ product.product.code }}</td>
              <td class="py-3 px-4 sm:px-6">{{ product.product.name }}</td>
              <td class="py-3 px-4 sm:px-6">{{ product.product.description }}</td>
              <td class="py-3 px-4 sm:px-6">{{ product.quantity }}</td>
              <td class="py-3 px-4 sm:px-6">${{ product.price }}</td>
              <td class="py-3 px-4 sm:px-6">${{ product.fee }}</td>
              <td class="py-3 px-4 sm:px-6">${{ (parseFloat(product.price) + parseFloat(product.fee)) * product.quantity }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="bg-gray-50">
              <td colspan="5" class="py-3 px-4 sm:px-6" />
              <td class="py-3 px-4 sm:px-6 font-semibold">Subtotal</td>
              <td class="py-3 px-4 sm:px-6">${{ (Number(order.subtotal) || 0).toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-50">
              <td colspan="5" class="py-3 px-4 sm:px-6" />
              <td class="py-3 px-4 sm:px-6 font-semibold">Fee</td>
              <td class="py-3 px-4 sm:px-6">${{ (Number(order.fee_total) || 0).toFixed(2) }}</td>
            </tr>
            <tr v-if="order.discount && Number(order.discount) > 0" class="bg-gray-50 text-red-600">
              <td colspan="5" class="py-3 px-4 sm:px-6" />
              <td class="py-3 px-4 sm:px-6 font-semibold">Descuento</td>
              <td class="py-3 px-4 sm:px-6">-${{ (Number(order.discount) || 0).toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-50 font-semibold">
              <td colspan="5" class="py-3 px-4 sm:px-6" />
              <td class="py-3 px-4 sm:px-6">Total</td>
              <td class="py-3 px-4 sm:px-6">${{ (Number(order.total) || 0).toFixed(2) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div class="mt-6 flex flex-col sm:flex-row gap-2">
        <button
          v-if="order.order_state === 'delivery'" class="w-full sm:w-auto px-4 py-2 bg-orange-500 text-white rounded-lg shadow-sm hover:bg-orange-600 transition-colors font-medium"
          @click="addDispatch"
        >
          <i class="fa fa-truck mr-2" /> Despachar productos
        </button>
      </div>
    </div>

    <!-- Sección de pagos -->
    <div v-if="canManagePayments" class="mb-6 p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-orange-100">
      <h2 class="text-xl font-bold mb-6">Pagos</h2>

      <payment-box
        v-if="order.order_state !== 'finish'" :quick="true" :payment_methods="payment_methods"
        :balance="order.total - order?.balance" :change="change" :order="order"
      />

      <!-- Tabla de pagos -->
      <div class="mt-6 overflow-x-auto rounded-lg border border-orange-100">
        <table class="w-full min-w-max">
          <thead class="bg-orange-50">
            <tr>
              <th class="py-4 px-4 sm:px-6 text-left">Método</th>
              <th class="py-4 px-4 sm:px-6 text-left">Monto</th>
              <th class="py-4 px-4 sm:px-6 text-left">Fecha</th>
              <th class="py-4 px-4 sm:px-6 text-left">Referencia</th>
              <th class="py-4 px-4 sm:px-6 text-left">Estatus</th>
              <th class="py-4 px-4 sm:px-6 text-left">Voucher</th>
              <th v-if="role === 'financial' || role === 'admin'" class="py-4 px-4 sm:px-6 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pay in order.pays" :key="pay.id" class="border-t hover:bg-orange-50 transition-colors">
              <td class="py-3 px-4 sm:px-6 capitalize">{{ pay.method }}</td>
              <td class="py-3 px-4 sm:px-6">
                {{ pay.amount }}
                <span v-if="pay.amount_bs > 0" class="text-gray-600">
                  (BsD. {{ pay.amount_bs }})
                </span>
              </td>
              <td class="py-3 px-4 sm:px-6">{{ formatDate(pay.created_at) }}</td>
              <td class="py-3 px-4 sm:px-6">{{ pay.reference }}</td>
              <td class="py-3 px-4 sm:px-6 capitalize">
                <span
                  :class="{
                    'text-orange-600 font-bold': pay.status === 'approve',
                    'text-yellow-600': pay.status === 'pending',
                    'text-red-600': pay.status === 'reject'
                  }"
                >
                  {{ pay.status === 'approve' ? 'Aprobado' : pay.status === 'pending' ? 'Pendiente' : 'Rechazado' }}
                </span>
              </td>
              <td class="py-3 px-4 sm:px-6">
                <a v-if="pay.voucher" :href="`/storage/${pay.voucher}`" target="_blank">
                  <img :src="`/storage/${pay.voucher}`" alt="Voucher" class="h-10 rounded shadow border" />
                </a>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td class="py-3 px-4 sm:px-6 flex items-center gap-2">
                <a
                  :href="`/orders/pay/${pay.id}/receipt`" target="_blank"
                  class="btn-blue px-4 py-2 hover:opacity-90 transition-opacity" title="Descargar Recibo"
                >
                  <i class="fa fa-file-invoice" />
                </a>
                <button class="btn-red px-4 py-2 hover:opacity-90 transition-opacity" title="Eliminar Pago" @click="deletePay(pay.id)">
                  <i class="fa fa-trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sección de despachos -->
    <!-- <div 
      v-if="showDispatchSection"
      class="mt-6 p-6 bg-white rounded-lg shadow"
    >
      <h2 class="text-xl font-bold mb-6">Despachos</h2>

      <table class="w-full">
        <thead>
          <tr>
            <th class="py-4 px-6 text-left">Concepto</th>
            <th class="py-4 px-6 text-left">Productos</th>
            <th class="py-4 px-6 text-left">Fecha</th>
            <th class="py-4 px-6 text-left">Responsable</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="dispatch in dispatches"
            :key="dispatch.id"
            class="border-t hover:bg-gray-50 transition-colors"
          >
            <td class="py-3 px-6">Se realizó el despacho al cliente</td>
            <td class="py-3 px-6">
              {{ dispatch.products.map(p => p.product.name).join(', ') }}
            </td>
            <td class="py-3 px-6">{{ formatDate(dispatch.date) }}</td>
            <td class="py-3 px-6">
              {{ getResponsibleName(dispatch.responsible) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div> -->

    <!-- Botón de anular orden -->
    <div v-if="canCancelOrder" class="mt-6 p-6 bg-white rounded-lg shadow text-right">
      <button class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors" @click="cancelOrder">
        Anular Orden
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import PaymentBox from '../../Shared/PaymentBox.vue'

export default {
  name: 'OrderShow',

  components: {
    Head,
    Link,
    TextInput,
    PaymentBox,
  },

  layout: Layout,

  props: {
    order: {
      type: Object,
      required: true,
    },
    dispatches: {
      type: Object,
      required: true,
    },
    payment_methods: {
      type: Object,
      required: true,
    },
    change: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      role: this.$parent.auth.user.role,
      form: this.$inertia.form({
        amount: 0,
        method: '',
        order_id: this.order.id,
        reference: '',
      }),
      products: this.$inertia.form({
        products: this.order.products,
        order_id: this.order.id,
      }),
    }
  },

  computed: {
    canManagePayments() {
      return ['financial', 'admin', 'sales'].includes(this.role) &&
        this.order.order_state !== 'cancel' &&
        this.order.order_state !== 'estimate'
    },

    showDispatchSection() {
      return this.order.order_state !== 'cancel' &&
        this.order.order_state !== 'estimate'
    },

    canCancelOrder() {
      return this.order.pays.length === 0 &&
        this.order.order_state !== 'cancel'
    },
  },

  methods: {
    getFieldTypeLabel(fieldType) {
      const labels = {
        'text': 'Texto',
        'number': 'Número',
        'date': 'Fecha',
        'select': 'Lista desplegable',
        'textarea': 'Área de texto',
        'boolean': 'Sí/No',
      }
      return labels[fieldType] || fieldType
    },
    shareOrder(type) {
      const url = `${window.location.origin}/orders/public/${this.order.uuid || this.order.id}`
      const msg = `Hola ${this.order.client?.name || ''}, aquí puedes consultar el detalle de tu compra: ${url}`
      if (type === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`)
      } else if (type === 'email') {
        try {
          this.$inertia.get(`/orders/send?order_id=${this.order.id}`, {
            method: 'get',
            preserveScroll: true,
            onSuccess: () => {
              this.$notify({ type: 'success', text: 'Correo enviado correctamente.' })
            },
            onError: () => {
              this.$notify({ type: 'error', text: 'No se pudo enviar el correo.' })
            },
          })
        } catch (e) {
          this.$notify({ type: 'error', text: 'No se pudo enviar el correo.' })
        }
      }
    },

    back() {
      window.history.back()
    },

    cancelOrder() {
      if (confirm('¿Está seguro que desea anular esta orden?')) {
        window.location.href = `/orders/${this.order.id}/cancel`
      }
    },

    addDispatch() {
      this.products.post('/orders/dispatch')
    },

    changeOrderState() {
      this.$inertia.get(`/orders/${this.order.id}/changeState`)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    getOrderStateLabel(state) {
      const labels = {
        estimate: 'PRESUPUESTO',
        cancel: 'ANULADA',
        request: 'PENDIENTE POR PAGO',
        finish: 'FINALIZADA',
        pay: 'PAGADA',
        delivery: 'EN ESPERA DE DESPACHO',
        por_iniciar: 'POR INICIAR',
        en_preparacion: 'EN PREPARACIÓN',
        pendiente_pago: 'PENDIENTE POR PAGO',
        finalizado: 'FINALIZADO',
      }
      return labels[state] || state
    },

    getResponsibleName(responsible) {
      if (!responsible) return ''
      return `${responsible.first_name} ${responsible.last_name}`
    },

    deletePay(payId) {
      if (confirm('¿Está seguro que desea eliminar este pago?')) {
        this.$inertia.delete(`/orders/pay/${payId}`)
      }
    },
  },
}
</script>

